import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { storeService } from '../services/storeService';
import { useInitEffect } from '../hooks/useInitEffect';
import { StoreProfile, StoreOrder, StoreOrderItem, StoreOrderPaymentMethod, PaymentMethod, PaymentMethodLabel } from '../models';
import { formatPhoneMask, unmaskPhone } from '../utils/phone';
import { safeImageUrl } from '../utils/url';

type Tab = 'dados' | 'pedidos';

const STATUS_COLORS: Record<number, string> = {
  1: 'bg-yellow-100 text-yellow-800',
  2: 'bg-green-100 text-green-800',
  3: 'bg-red-100 text-red-800',
  4: 'bg-blue-100 text-blue-800',
  5: 'bg-gray-100 text-gray-700',
  6: 'bg-orange-100 text-orange-800',
};

const GENDER_OPTIONS = [
  { value: 1, label: 'Menino' },
  { value: 2, label: 'Menina' },
  { value: 3, label: 'Neutro' },
];

const PAYMENT_METHOD_EMOJI: Record<PaymentMethod, string> = {
  [PaymentMethod.CreditCard]: '💳',
  [PaymentMethod.DebitCard]: '💳',
  [PaymentMethod.Pix]: '📱',
  [PaymentMethod.Cash]: '💵',
  [PaymentMethod.CreditStore]: '🏪',
  [PaymentMethod.BankTransfer]: '🏦',
  [PaymentMethod.Promissory]: '🏦',
};

function formatDate(iso?: string) {
  if (!iso) return '—';
  try {
    // Remove a parte de hora (se tiver T00:00:00, fica só YYYY-MM-DD)
    const dateOnly = iso.split('T')[0];
    const [year, month, day] = dateOnly.split('-');
    if (year && month && day) {
      const date = new Date(Number(year), Number(month) - 1, Number(day));
      return date.toLocaleDateString('pt-BR');
    }
  } catch {
    return '—';
  }
  return '—';
}

function formatCurrency(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function toInputDate(iso?: string) {
  if (!iso) return '';
  return iso.split('T')[0];
}

export function ProfilePage() {
  const { isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialTab = (searchParams.get('tab') === 'pedidos' ? 'pedidos' : 'dados') as Tab;
  const [tab, setTab] = useState<Tab>(initialTab);
  const [profile, setProfile] = useState<StoreProfile | null>(null);
  const [orders, setOrders] = useState<StoreOrder[]>([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const [editingOrder, setEditingOrder] = useState<StoreOrder | null>(null);
  const [editItems, setEditItems] = useState<StoreOrderItem[]>([]);
  const [editPayments, setEditPayments] = useState<StoreOrderPaymentMethod[]>([]);
  const [editObservation, setEditObservation] = useState('');
  const [savingOrderEdit, setSavingOrderEdit] = useState(false);
  const [orderEditError, setOrderEditError] = useState('');
  const [newPaymentMethod, setNewPaymentMethod] = useState<number>(PaymentMethod.Pix);
  const [newPaymentAmountCents, setNewPaymentAmountCents] = useState<number>(0);
  const [newPaymentDisplay, setNewPaymentDisplay] = useState<string>('');

  const [cancelingOrderId, setCancelingOrderId] = useState<number | null>(null);
  const [confirmCancelOrderId, setConfirmCancelOrderId] = useState<number | null>(null);
  const [loadingEditOrder, setLoadingEditOrder] = useState(false);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    babyName: '',
    babyBirthdate: '',
    babyGender: '' as string,
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    complement: '',
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const handleUnauthorized = () => {
      setUnauthorized(true);
      logout();
    };

    window.addEventListener('unauthorized', handleUnauthorized);
    return () => window.removeEventListener('unauthorized', handleUnauthorized);
  }, [logout]);

  useInitEffect(() => {
    storeService.getProfile()
      .then((p) => {
        setProfile(p);
        setForm({
          name: p.name ?? '',
          phone: p.phone ? formatPhoneMask(p.phone) : '',
          babyName: p.babyName ?? '',
          babyBirthdate: toInputDate(p.babyBirthdate),
          babyGender: p.babyGender != null ? String(p.babyGender) : '',
          street: p.street ?? '',
          number: p.number ?? '',
          neighborhood: p.neighborhood ?? '',
          city: p.city ?? '',
          state: p.state ?? '',
          zipCode: p.zipCode ?? '',
          complement: p.complement ?? '',
        });
      })
      .catch((error) => {
        if (error?.response?.status === 401) {
          setUnauthorized(true);
          logout();
        }
      })
      .finally(() => setLoadingProfile(false));
  });

  useEffect(() => {
    if (tab === 'pedidos' && orders.length === 0) {
      setLoadingOrders(true);
      storeService.getOrders()
        .then(setOrders)
        .catch((error) => {
          if (error?.response?.status === 401) {
            setUnauthorized(true);
            logout();
          }
        })
        .finally(() => setLoadingOrders(false));
    }
  }, [tab, logout]);

  function handleFormChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function handlePhoneFormChange(e: React.ChangeEvent<HTMLInputElement>) {
    const masked = formatPhoneMask(e.target.value);
    setForm((prev) => ({ ...prev, phone: masked }));
  }

  function cancelEdit() {
    if (!profile) return;
    setForm((prev) => ({
      ...prev,
      name: profile.name ?? '',
      phone: profile.phone ? formatPhoneMask(profile.phone) : '',
      babyName: profile.babyName ?? '',
      babyBirthdate: toInputDate(profile.babyBirthdate),
      babyGender: profile.babyGender != null ? String(profile.babyGender) : '',
      street: profile.street ?? '',
      number: profile.number ?? '',
      neighborhood: profile.neighborhood ?? '',
      city: profile.city ?? '',
      state: profile.state ?? '',
      zipCode: profile.zipCode ?? '',
      complement: profile.complement ?? '',
    }));
    setEditing(false);
    setSaveError('');
  }

  async function handleSave() {
    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      await storeService.updateProfile({
        name: form.name,
        phone: unmaskPhone(form.phone),
        babyName: form.babyName || undefined,
        babyBirthdate: form.babyBirthdate || undefined,
        babyGender: form.babyGender ? Number(form.babyGender) : undefined,
        street: form.street || undefined,
        number: form.number || undefined,
        neighborhood: form.neighborhood || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        zipCode: form.zipCode || undefined,
        complement: form.complement || undefined,
      });
      setProfile((prev) => prev ? {
        ...prev,
        name: form.name,
        phone: unmaskPhone(form.phone),
        babyName: form.babyName || undefined,
        babyBirthdate: form.babyBirthdate || undefined,
        babyGender: form.babyGender ? Number(form.babyGender) : undefined,
        street: form.street || undefined,
        number: form.number || undefined,
        neighborhood: form.neighborhood || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        zipCode: form.zipCode || undefined,
        complement: form.complement || undefined,
      } : prev);
      setEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      setSaveError('Não foi possível salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  }

  async function handleCepSearch() {
    const cep = form.zipCode.replace(/\D/g, '');
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const address = await storeService.lookupCep(cep);
      setForm((prev) => ({
        ...prev,
        street: address.street ?? prev.street,
        number: '', // Limpa o número após buscar CEP
        neighborhood: address.neighborhood ?? prev.neighborhood,
        city: address.city ?? prev.city,
        state: address.state ?? prev.state,
      }));
    } catch {
      // CEP não encontrado — manter campos como estão
    } finally {
      setCepLoading(false);
    }
  }

  async function handlePasswordChange() {
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError('As senhas não coincidem.');
      return;
    }
    setSavingPassword(true);
    setPasswordError('');
    setPasswordSuccess(false);
    try {
      await storeService.updateProfile({
        name: profile!.name,
        phone: profile!.phone,
        currentPassword: passwordForm.current,
        newPassword: passwordForm.next,
      });
      setPasswordForm({ current: '', next: '', confirm: '' });
      setPasswordSuccess(true);
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: string[] } } };
      setPasswordError(err?.response?.data?.errors?.[0] ?? 'Não foi possível alterar a senha.');
    } finally {
      setSavingPassword(false);
    }
  }

  async function cancelOrder(orderId: number) {
    setCancelingOrderId(orderId);
    try {
      await storeService.cancelOrder(orderId);
      const updated = await storeService.getOrders();
      setOrders(updated);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: string[] } } };
      alert(err?.response?.data?.errors?.[0] ?? 'Não foi possível cancelar o pedido.');
    } finally {
      setCancelingOrderId(null);
    }
  }

  function openEditOrder(order: StoreOrder) {
    setLoadingEditOrder(true);
    setTimeout(() => {
      setEditingOrder(order);
      setEditItems(order.items.map((i) => ({ ...i })));
      setEditPayments(order.paymentMethods.map((p) => ({ ...p })));
      setEditObservation(order.observation ?? '');
      setOrderEditError('');
      setNewPaymentAmountCents(0);
      setNewPaymentDisplay('');
      setNewPaymentMethod(PaymentMethod.Pix);
      setLoadingEditOrder(false);
    }, 0);
  }

  function closeEditOrder() {
    setEditingOrder(null);
  }

  function removeEditItem(idx: number) {
    setEditItems((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleNewPaymentAmountInput(e: React.ChangeEvent<HTMLInputElement>) {
    const digits = e.target.value.replace(/\D/g, '');
    const cents = parseInt(digits || '0', 10);
    setNewPaymentAmountCents(cents);
    const reais = cents / 100;
    setNewPaymentDisplay(
      reais === 0 ? '' : reais.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    );
  }

  function addEditPayment() {
    const amount = newPaymentAmountCents / 100;
    if (!amount || amount <= 0) return;
    setEditPayments((prev) => [
      ...prev,
      { id: 0, paymentMethod: newPaymentMethod, paymentMethodLabel: PaymentMethodLabel[newPaymentMethod as PaymentMethod], amountPaid: amount },
    ]);
    setNewPaymentAmountCents(0);
    setNewPaymentDisplay('');
  }

  function removeEditPayment(idx: number) {
    setEditPayments((prev) => prev.filter((_, i) => i !== idx));
  }

  async function saveOrderEdit() {
    if (!editingOrder) return;
    if (editItems.length === 0) {
      setOrderEditError('O pedido deve ter ao menos um item.');
      return;
    }
    setSavingOrderEdit(true);
    setOrderEditError('');
    try {
      await storeService.updateOrder(editingOrder.id, {
        observation: editObservation || undefined,
        items: editItems.map((i) => ({
          id: i.id,
          productId: i.productId,
          productVariantId: i.productVariantId,
          quantity: i.quantity,
          unitPrice: i.unitPrice,
          totalPrice: i.totalPrice,
        })),
        paymentMethods: editPayments.map((p) => ({
          id: p.id,
          paymentMethod: p.paymentMethod,
          amountPaid: p.amountPaid,
        })),
      });
      const updated = await storeService.getOrders();
      setOrders(updated);
      closeEditOrder();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: string[] } } };
      setOrderEditError(err?.response?.data?.errors?.[0] ?? 'Não foi possível salvar o pedido.');
    } finally {
      setSavingOrderEdit(false);
    }
  }

  if (!isAuthenticated) return null;

  if (unauthorized) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <div className="bg-white rounded-2xl border border-champagne-dark shadow-sm p-8">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4v2m0 4v2M9 9h.01M15 9h.01M9 15h.01M15 15h.01" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-text-dark mb-2">Acesso Expirado</h2>
          <p className="text-sm text-text-light mb-6">
            Sua sessão expirou ou você não tem permissão para acessar esta página. Por favor, faça login novamente.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/login')}
              className="w-full px-4 py-2.5 text-sm font-medium text-white bg-chocolate hover:bg-chocolate-dark rounded-full transition-colors"
            >
              Ir para Login
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full px-4 py-2.5 text-sm font-medium text-chocolate border border-chocolate hover:bg-chocolate/5 rounded-full transition-colors"
            >
              Voltar ao Catálogo
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-text-dark mb-1">Minha Conta</h1>
      <p className="text-sm text-text-light mb-6">Gerencie seus dados e acompanhe seus pedidos</p>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-champagne-dark">
        {(['dados', 'pedidos'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={[
              'px-5 py-2.5 text-sm font-medium transition-colors -mb-px border-b-2',
              tab === t
                ? 'border-chocolate text-chocolate'
                : 'border-transparent text-text-light hover:text-text-medium',
            ].join(' ')}
          >
            {t === 'dados' ? 'Meus Dados' : 'Meus Pedidos'}
          </button>
        ))}
      </div>

      {/* Tab: Dados */}
      {tab === 'dados' && (
        <div className="space-y-4">
          {/* Card: Dados Pessoais */}
          <div className="bg-white rounded-2xl border border-champagne-dark shadow-sm">
            {loadingProfile ? (
              <div className="p-8 text-center text-text-light text-sm">Carregando...</div>
            ) : profile ? (
              <div className="divide-y divide-champagne">
                {/* Header do cartão */}
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-chocolate/10 flex items-center justify-center text-chocolate font-bold text-lg">
                      {profile.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-text-dark">{profile.name}</p>
                      <p className="text-xs text-text-light">{profile.email}</p>
                    </div>
                  </div>
                  {profile.creditValue != null && profile.creditValue > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-text-light">Crédito disponível</p>
                      <p className="text-base font-bold text-olivine-dark">{formatCurrency(profile.creditValue)}</p>
                    </div>
                  )}
                </div>

                {/* Campos: Dados Pessoais */}
                <div className="px-6 py-5">
                  <h3 className="font-semibold text-text-dark mb-4">Dados Pessoais</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Nome completo">
                      {editing ? (
                        <input name="name" value={form.name} onChange={handleFormChange}
                          className="input-field" />
                      ) : profile.name}
                    </Field>

                    <Field label="Telefone">
                      {editing ? (
                        <input name="phone" type="tel" value={form.phone} onChange={handlePhoneFormChange}
                          placeholder="(00) 00000-0000" className="input-field" />
                      ) : profile.phone || '—'}
                    </Field>

                    <Field label="E-mail">
                      <span className="text-text-light">{profile.email}</span>
                    </Field>

                    <Field label="Nome do bebê">
                      {editing ? (
                        <input name="babyName" value={form.babyName} onChange={handleFormChange}
                          className="input-field" placeholder="Opcional" />
                      ) : profile.babyName || '—'}
                    </Field>

                    <Field label="Nascimento do bebê">
                      {editing ? (
                        <input type="date" name="babyBirthdate" value={form.babyBirthdate}
                          onChange={handleFormChange} className="input-field" />
                      ) : formatDate(profile.babyBirthdate)}
                    </Field>

                    <Field label="Gênero do bebê">
                      {editing ? (
                        <select name="babyGender" value={form.babyGender} onChange={handleFormChange}
                          className="input-field">
                          <option value="">Não informado</option>
                          {GENDER_OPTIONS.map((g) => (
                            <option key={g.value} value={g.value}>{g.label}</option>
                          ))}
                        </select>
                      ) : GENDER_OPTIONS.find((g) => g.value === profile.babyGender)?.label || '—'}
                    </Field>
                  </div>
                </div>

                {/* Divisor */}
                <div className="border-t border-champagne"></div>

                {/* Campos: Endereço */}
                <div className="px-6 py-5">
                  <h3 className="font-semibold text-text-dark mb-4">Endereço de Entrega</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {editing ? (
                      <>
                        {/* CEP com botão de busca */}
                        <div>
                          <p className="text-xs font-medium text-text-light uppercase tracking-wide mb-1">CEP</p>
                          <div className="flex gap-2">
                            <input
                              name="zipCode"
                              value={form.zipCode}
                              onChange={handleFormChange}
                              placeholder="00000-000"
                              className="input-field flex-1"
                            />
                            <button
                              onClick={handleCepSearch}
                              disabled={cepLoading || form.zipCode.replace(/\D/g, '').length !== 8}
                              className="px-3 py-2 text-sm text-white bg-chocolate hover:bg-chocolate-dark rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                            >
                              {cepLoading ? '...' : 'Buscar'}
                            </button>
                          </div>
                        </div>

                        <div>
                          <p className="text-xs font-medium text-text-light uppercase tracking-wide mb-1">Rua</p>
                          <input
                            name="street"
                            value={form.street}
                            onChange={handleFormChange}
                            className="input-field"
                          />
                        </div>

                        <div>
                          <p className="text-xs font-medium text-text-light uppercase tracking-wide mb-1">Número</p>
                          <input
                            name="number"
                            value={form.number}
                            onChange={handleFormChange}
                            className="input-field"
                          />
                        </div>

                        <div>
                          <p className="text-xs font-medium text-text-light uppercase tracking-wide mb-1">Complemento</p>
                          <input
                            name="complement"
                            value={form.complement}
                            onChange={handleFormChange}
                            className="input-field"
                          />
                        </div>

                        <div>
                          <p className="text-xs font-medium text-text-light uppercase tracking-wide mb-1">Bairro</p>
                          <input
                            name="neighborhood"
                            value={form.neighborhood}
                            onChange={handleFormChange}
                            className="input-field"
                          />
                        </div>

                        <div>
                          <p className="text-xs font-medium text-text-light uppercase tracking-wide mb-1">Cidade</p>
                          <input
                            name="city"
                            value={form.city}
                            onChange={handleFormChange}
                            className="input-field"
                          />
                        </div>

                        <div>
                          <p className="text-xs font-medium text-text-light uppercase tracking-wide mb-1">Estado</p>
                          <input
                            name="state"
                            value={form.state}
                            onChange={handleFormChange}
                            className="input-field"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <Field label="Endereço">
                          {profile.street ? `${profile.street}, ${profile.number}` : '—'}
                        </Field>
                        <Field label="Complemento">
                          {profile.complement || '—'}
                        </Field>
                        <Field label="Bairro">
                          {profile.neighborhood || '—'}
                        </Field>
                        <Field label="Cidade/Estado">
                          {profile.city && profile.state ? `${profile.city}, ${profile.state}` : '—'}
                        </Field>
                        <Field label="CEP">
                          {profile.zipCode || '—'}
                        </Field>
                      </>
                    )}
                  </div>
                </div>

                {/* Ações */}
                <div className="px-6 py-4 border-t border-champagne flex items-center justify-between">
                  <div>
                    {saveSuccess && (
                      <p className="text-sm text-green-600 font-medium">Dados atualizados com sucesso!</p>
                    )}
                    {saveError && (
                      <p className="text-sm text-red-600">{saveError}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {editing ? (
                      <>
                        <button onClick={cancelEdit}
                          className="px-4 py-2 text-sm text-text-medium border border-champagne-dark rounded-full hover:border-text-light transition-colors">
                          Cancelar
                        </button>
                        <button onClick={handleSave} disabled={saving}
                          className="px-4 py-2 text-sm text-white bg-chocolate hover:bg-chocolate-dark rounded-full transition-colors disabled:opacity-60">
                          {saving ? 'Salvando...' : 'Salvar'}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => { logout(); navigate('/login'); }}
                          className="px-4 py-2 text-sm text-text-light border border-champagne-dark rounded-full hover:border-text-light transition-colors">
                          Sair
                        </button>
                        <button onClick={() => setEditing(true)}
                          className="px-4 py-2 text-sm text-white bg-chocolate hover:bg-chocolate-dark rounded-full transition-colors">
                          Editar dados
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-text-light text-sm">
                Não foi possível carregar o perfil.
              </div>
            )}
          </div>

          {/* Card: Alterar Senha */}
          {!loadingProfile && profile && (
            <div className="bg-white rounded-2xl border border-champagne-dark shadow-sm">
              <div className="flex items-center justify-between px-6 py-4 border-b border-champagne">
                <div>
                  <h2 className="font-semibold text-text-dark">Alterar Senha</h2>
                  <p className="text-xs text-text-light mt-0.5">Atualize sua senha de acesso</p>
                </div>
              </div>

              <div className="px-6 py-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <p className="text-xs font-medium text-text-light uppercase tracking-wide mb-1">Senha atual</p>
                    <input
                      type="password"
                      value={passwordForm.current}
                      onChange={(e) => setPasswordForm((prev) => ({ ...prev, current: e.target.value }))}
                      className="input-field"
                      autoComplete="current-password"
                    />
                  </div>

                  <div>
                    <p className="text-xs font-medium text-text-light uppercase tracking-wide mb-1">Nova senha</p>
                    <input
                      type="password"
                      value={passwordForm.next}
                      onChange={(e) => setPasswordForm((prev) => ({ ...prev, next: e.target.value }))}
                      className="input-field"
                      autoComplete="new-password"
                    />
                  </div>

                  <div>
                    <p className="text-xs font-medium text-text-light uppercase tracking-wide mb-1">Confirmar nova senha</p>
                    <input
                      type="password"
                      value={passwordForm.confirm}
                      onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirm: e.target.value }))}
                      className="input-field"
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <div>
                    {passwordError && (
                      <p className="text-sm text-red-600">{passwordError}</p>
                    )}
                    {passwordSuccess && (
                      <p className="text-sm text-green-600 font-medium">Senha alterada com sucesso!</p>
                    )}
                  </div>
                  <button
                    onClick={handlePasswordChange}
                    disabled={savingPassword || !passwordForm.current || !passwordForm.next || !passwordForm.confirm}
                    className="px-4 py-2 text-sm text-white bg-chocolate hover:bg-chocolate-dark rounded-full transition-colors disabled:opacity-60"
                  >
                    {savingPassword ? 'Alterando...' : 'Alterar senha'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Pedidos */}
      {tab === 'pedidos' && (
        <div className="space-y-4">
          {loadingOrders ? (
            <div className="bg-white rounded-2xl border border-champagne-dark shadow-sm p-8 text-center text-text-light text-sm">
              Carregando pedidos...
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-white rounded-2xl border border-champagne-dark shadow-sm p-10 text-center">
              <p className="text-text-medium font-medium mb-1">Nenhum pedido encontrado</p>
              <p className="text-sm text-text-light">Suas compras aparecerão aqui.</p>
            </div>
          ) : (
            orders.map((order) => (
              <div key={order.id} className="bg-white rounded-2xl border border-champagne-dark shadow-sm overflow-hidden">
                {/* Cabeçalho do pedido */}
                <div className="flex items-center justify-between px-5 py-3 bg-champagne/40 border-b border-champagne">
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-semibold text-text-dark">Pedido #{order.id}</p>
                    <span className={[
                      'text-[11px] font-medium px-2.5 py-0.5 rounded-full',
                      STATUS_COLORS[order.status] ?? 'bg-gray-100 text-gray-600',
                    ].join(' ')}>
                      {order.statusLabel}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {order.status === 6 && (
                      <>
                        <button
                          onClick={() => setConfirmCancelOrderId(order.id)}
                          disabled={cancelingOrderId === order.id}
                          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-60"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          {cancelingOrderId === order.id ? '...' : 'Cancelar'}
                        </button>
                        <button
                          onClick={() => openEditOrder(order)}
                          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white bg-chocolate hover:bg-chocolate-dark transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H9v-2.414a2 2 0 01.586-1.414z" />
                          </svg>
                          Editar
                        </button>
                      </>
                    )}
                    <div className="text-right">
                      <p className="text-xs text-text-light">{formatDate(order.saleDate)}</p>
                      <p className="text-sm font-bold text-text-dark">{formatCurrency(order.totalValue)}</p>
                    </div>
                  </div>
                </div>

                {/* Itens */}
                <ul className="divide-y divide-champagne/60">
                  {order.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 px-5 py-3">
                      {item.imageUrl ? (
                        <img src={safeImageUrl(item.imageUrl)} alt={item.productName}
                          className="w-12 h-12 rounded-lg object-cover bg-champagne shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-champagne shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-dark truncate">{item.productName}</p>
                        {item.variantLabel && (
                          <p className="text-xs text-text-light">{item.variantLabel}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs text-text-light">{item.quantity}x {formatCurrency(item.unitPrice)}</p>
                        <p className="text-sm font-semibold text-text-dark">{formatCurrency(item.totalPrice)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      )}

      {/* Confirm cancel order modal */}
      {confirmCancelOrderId !== null && (
        <div className="fixed inset-0 z-60 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmCancelOrderId(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-base font-semibold text-text-dark mb-2">
              Cancelar Pedido #{confirmCancelOrderId}
            </h2>
            <p className="text-sm text-text-medium mb-6">
              Tem certeza que deseja cancelar este pedido? Essa ação não pode ser desfeita.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmCancelOrderId(null)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-text-medium border border-champagne-dark rounded-full hover:border-text-light transition-colors"
              >
                Voltar
              </button>
              <button
                onClick={() => {
                  const id = confirmCancelOrderId;
                  setConfirmCancelOrderId(null);
                  cancelOrder(id);
                }}
                disabled={cancelingOrderId === confirmCancelOrderId}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-full transition-colors disabled:opacity-60"
              >
                Sim, cancelar pedido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit order overlay */}
      {editingOrder && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeEditOrder} />
          <div className="relative w-full sm:max-w-lg bg-white sm:rounded-2xl rounded-t-2xl shadow-xl flex flex-col max-h-[90dvh]">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-champagne shrink-0">
              <div>
                <h2 className="font-semibold text-text-dark">Editar Pedido #{editingOrder.id}</h2>
                <p className="text-xs text-text-light mt-0.5">Ajuste itens e formas de pagamento</p>
              </div>
              <button
                onClick={closeEditOrder}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-champagne transition-colors text-text-light"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
              {loadingEditOrder ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="animate-spin border-2 border-chocolate border-t-transparent rounded-full w-8 h-8 mx-auto" />
                  <p className="text-sm text-text-light">Carregando pedido...</p>
                </div>
              ) : null}
              {!loadingEditOrder && (
              <>
              {/* Items */}
              <div>
                <p className="text-xs font-semibold text-text-light uppercase tracking-wide mb-2">Itens</p>
                <p className="text-xs text-text-light bg-champagne/40 rounded-lg px-3 py-2 mb-3">
                  Para adicionar novos itens, cancele este pedido e faça um novo.
                </p>
                {editItems.length === 0 ? (
                  <p className="text-sm text-text-light text-center py-4">Todos os itens foram removidos.</p>
                ) : (
                  <ul className="space-y-2">
                    {editItems.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-3 bg-champagne/30 rounded-xl px-3 py-2.5">
                        {item.imageUrl ? (
                          <img src={safeImageUrl(item.imageUrl)} alt={item.productName}
                            className="w-10 h-10 rounded-lg object-cover bg-champagne shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-champagne shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-text-dark truncate">{item.productName}</p>
                          {item.variantLabel && (
                            <p className="text-xs text-text-light">{item.variantLabel}</p>
                          )}
                          <p className="text-xs text-text-light">× {item.quantity} &nbsp;{formatCurrency(item.unitPrice)} cada</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-sm font-semibold text-text-dark">{formatCurrency(item.totalPrice)}</span>
                          <button
                            onClick={() => removeEditItem(idx)}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-red-400 hover:bg-red-50 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-2 flex justify-between text-xs text-text-light">
                  <span>Subtotal</span>
                  <span className="font-semibold text-text-dark">
                    {formatCurrency(editItems.reduce((s, i) => s + i.totalPrice, 0))}
                  </span>
                </div>
              </div>

              {/* Payment methods */}
              <div>
                <p className="text-xs font-semibold text-text-light uppercase tracking-wide mb-1">Pagamento</p>
                <div className="mb-3">
                  <p className="text-sm font-medium text-text-dark">Pagamentos atuais</p>
                  <p className="text-xs text-text-light mt-0.5">Você pode remover um pagamento existente e adicionar um novo. É possível ter mais de uma forma de pagamento.</p>
                </div>
                {editPayments.length > 0 ? (
                  <ul className="space-y-2 mb-4">
                    {editPayments.map((pm, idx) => (
                      <li key={idx} className="flex items-center justify-between bg-champagne/30 rounded-xl px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-base leading-none">{PAYMENT_METHOD_EMOJI[pm.paymentMethod as PaymentMethod] ?? '💳'}</span>
                          <span className="text-sm text-text-dark">{pm.paymentMethodLabel}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-text-dark">{formatCurrency(pm.amountPaid)}</span>
                          <button
                            onClick={() => removeEditPayment(idx)}
                            className="w-6 h-6 rounded-full flex items-center justify-center text-red-400 hover:bg-red-50 transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-text-light text-center py-2 mb-4">Nenhum pagamento registrado.</p>
                )}
                {/* Add payment */}
                <div>
                  <p className="text-xs font-medium text-text-dark mb-2">Adicionar pagamento</p>
                  <div className="flex gap-2">
                    <select
                      value={newPaymentMethod}
                      onChange={(e) => setNewPaymentMethod(Number(e.target.value))}
                      className="flex-1 text-sm border border-champagne-dark rounded-lg px-2 py-1.5 bg-white text-text-dark focus:outline-none focus:ring-1 focus:ring-chocolate/30"
                    >
                      {Object.entries(PaymentMethodLabel).map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="R$ 0,00"
                      value={newPaymentDisplay}
                      onChange={handleNewPaymentAmountInput}
                      className="w-28 text-sm border border-champagne-dark rounded-lg px-2 py-1.5 text-text-dark focus:outline-none focus:ring-1 focus:ring-chocolate/30"
                    />
                    <button
                      onClick={addEditPayment}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-chocolate hover:bg-chocolate-dark rounded-lg transition-colors whitespace-nowrap"
                    >
                      + Adicionar
                    </button>
                  </div>
                </div>
              </div>

              {/* Observation */}
              <div>
                <p className="text-xs font-semibold text-text-light uppercase tracking-wide mb-2">Observação</p>
                <textarea
                  value={editObservation}
                  onChange={(e) => setEditObservation(e.target.value)}
                  rows={2}
                  placeholder="Observação sobre o pedido (opcional)"
                  className="w-full text-sm border border-champagne-dark rounded-xl px-3 py-2 text-text-dark resize-none focus:outline-none focus:ring-1 focus:ring-chocolate/30"
                />
              </div>
              </>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-champagne shrink-0">
              {orderEditError && (
                <p className="text-sm text-red-600 mb-3">{orderEditError}</p>
              )}
              <div className="flex gap-3">
                <button
                  onClick={closeEditOrder}
                  disabled={savingOrderEdit}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-text-medium border border-champagne-dark rounded-full hover:border-text-light transition-colors disabled:opacity-60"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveOrderEdit}
                  disabled={savingOrderEdit}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-chocolate hover:bg-chocolate-dark rounded-full transition-colors disabled:opacity-60"
                >
                  {savingOrderEdit ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-text-light uppercase tracking-wide mb-1">{label}</p>
      <div className="text-sm text-text-dark">{children}</div>
    </div>
  );
}
